import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  Form,
  FormGroup,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Row,
  Spinner,
  Modal,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import api from "../../services/client/api";
import cookie from "../../services/cookie/index";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faSearch } from "@fortawesome/free-solid-svg-icons";

import { FormWrapperForm, InputView, InputData } from "./styles";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

export default function PermissionCard({ onSuccess }) {
  const [username, setUsername] = useState("");
  const [token, setToken] = useState("");
  const [code, setCode] = useState("");
  const [checkingCache, setCheckingCache] = useState(true);
  const [fields, setFields] = useState({
    NomPaciente: "",
    CPF: "",
  });
  const [feedback, setFeedback] = useState({
    open: false,
    title: "",
    message: "",
    type: "warning", // warning | danger | success
    allowNotification: false,
  });
  const [inProgress, setInProgress] = useState(false);

  // 👇 garante leitura após mount
  useEffect(() => {
    const value = cookie().get("username");
    const token = cookie().get("token");
    const code = cookie().get("code");
    setUsername(value || "");
    setToken(token);
    setCode(code);
  }, []);
  useEffect(() => {
    const requirePermission = cookie().get("requirePermission") === "true";

    if (!requirePermission) {
      setCheckingCache(false);
      return;
    }

    const cachedReports = localStorage.getItem("reports");

    // Não existe cache, segue fluxo normal
    if (!cachedReports) {
      setCheckingCache(false);
      return;
    }

    try {
      const cached = JSON.parse(cachedReports);

      // Existe cache e possui exames
      if (cached?.exams?.length > 0) {
        onSuccess(cached);
        return;
      }
    } catch (err) {
      console.error(err);
      localStorage.removeItem("reports");
    }

    setCheckingCache(false);
  }, [onSuccess]);

  const isValidCPF = (cpf) => {
    cpf = cpf.replace(/\D/g, "");

    if (cpf.length !== 11) return false;

    // Elimina CPFs com todos os dígitos iguais
    if (/^(\d)\1+$/.test(cpf)) return false;

    let sum = 0;

    // Primeiro dígito
    for (let i = 0; i < 9; i++) {
      sum += Number(cpf.charAt(i)) * (10 - i);
    }

    let remainder = (sum * 10) % 11;
    if (remainder === 10) remainder = 0;

    if (remainder !== Number(cpf.charAt(9))) {
      return false;
    }

    sum = 0;

    // Segundo dígito
    for (let i = 0; i < 10; i++) {
      sum += Number(cpf.charAt(i)) * (11 - i);
    }

    remainder = (sum * 10) % 11;
    if (remainder === 10) remainder = 0;

    return remainder === Number(cpf.charAt(10));
  };

  async function handleSubmit(e) {
    e.preventDefault();
    const cpf = onlyNumbers(fields.CPF);

    // Se começou a preencher, precisa estar completo
    if (cpf.length > 0 && cpf.length < 11) {
      setFeedback({
        open: true,
        type: "warning",
        title: "CPF incompleto",
        message:
          "Informe o CPF completo do paciente ou deixe o campo em branco para realizar a busca apenas pelo nome.",
        allowNotification: false,
      });
      return;
    }

    // Se informou os 11 dígitos, precisa ser um CPF válido
    if (cpf.length === 11 && !isValidCPF(cpf)) {
      setFeedback({
        open: true,
        type: "warning",
        title: "CPF inválido",
        message:
          "O CPF informado é inválido. Verifique os números digitados e tente novamente.",
        allowNotification: false,
      });
      return;
    }

    setInProgress(true);

    setInProgress(true);

    const payload = {
      ...fields,
      CPF: onlyNumbers(fields.CPF),
      username,
    };

    const response = await api().user().getPatient(payload, token);

    if (response.status === 200) {
      const requirePermission = cookie().get("requirePermission") === "true";

      if (requirePermission) {
        localStorage.setItem("reports", JSON.stringify(response.output));
      }

      onSuccess(response.output);

      setInProgress(false);
      return;
    }

    if (
      response.message ===
        "Mais de um paciente encontrado. Necessário fornecer mais informações." ||
      response.message === "Nenhum paciente encontrado com os dados informados"
    ) {
      const success = await handleRegisterOcurrence(false);

      if (success) {
        setFeedback({
          open: true,
          type: "success",
          title: "Solicitação registrada",
          message:
            "Não foi possível localizar o paciente com os dados informados neste momento. Já notificamos nossa equipe de suporte, que analisará o caso e realizará as verificações necessárias. Entraremos em contato caso seja preciso obter mais informações.",
          allowNotification: false,
        });
      } else {
        setFeedback({
          open: true,
          type: "danger",
          title: "Erro ao enviar",
          message:
            "Não foi possível enviar a ocorrência. Tente novamente em alguns instantes.",
          allowNotification: false,
        });
      }

      setInProgress(false);
      return;
    }
  }
  async function handleRegisterOcurrence(showFeedback = true) {
    try {
      const payload = {
        NomPaciente: fields.NomPaciente,
        code,
        CPF: onlyNumbers(fields.CPF),
        username,
      };

      const response = await api().user().registerOccurrence(payload, token);

      if (response.status === 200 || response.status === 409) {
        if (showFeedback) {
          setFeedback({
            open: true,
            type: "success",
            title: "Solicitação registrada",
            message:
              "Não foi possível localizar o paciente com os dados informados neste momento. Já notificamos nossa equipe de suporte, que analisará o caso e realizará as verificações necessárias. Entraremos em contato caso seja preciso obter mais informações.",
            allowNotification: false,
          });
        }

        return true;
      }

      return false;
    } catch (error) {
      console.error(error);

      if (showFeedback) {
        setFeedback({
          open: true,
          type: "danger",
          title: "Erro ao enviar",
          message:
            "Não foi possível enviar a ocorrência. Tente novamente em alguns instantes.",
          allowNotification: false,
        });
      }

      return false;
    }
  }

  const formatCPF = (value) => {
    return value
      .replace(/\D/g, "") // remove tudo que não é número
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
      .slice(0, 14);
  };

  const onlyNumbers = (value) => value.replace(/\D/g, "");
  if (checkingCache) {
    return null; // ou um Spinner
  }
  return (
    <FormWrapperForm>
      <Form onSubmit={handleSubmit}>
        <h4
          style={{
            marginBottom: "30px",
          }}
        >
          Informe os dados do paciente para acessar os exames.
        </h4>

        <Row>
          <Col md={6}>
            <FormGroup>
              <span
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontWeight: 600,
                }}
              >
                Nome completo
              </span>

              <InputGroup>
                <InputData
                  required
                  type="text"
                  placeholder="Digite o nome completo do paciente"
                  value={fields.NomPaciente}
                  onChange={(e) =>
                    setFields({
                      ...fields,
                      NomPaciente: e.target.value,
                    })
                  }
                />
              </InputGroup>
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <span
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontWeight: 600,
                }}
              >
                CPF
              </span>

              <InputGroup>
                <InputData
                  type="text"
                  placeholder="000.000.000-00"
                  maxLength={14}
                  value={fields.CPF}
                  onChange={(e) =>
                    setFields({
                      ...fields,
                      CPF: formatCPF(e.target.value),
                    })
                  }
                />
              </InputGroup>
            </FormGroup>
          </Col>
        </Row>

        {inProgress ? (
          <Spinner color="primary" />
        ) : (
          <Button color="primary" type="submit">
            <FontAwesomeIcon icon={faSearch} /> Buscar
          </Button>
        )}
      </Form>
      <Modal
        isOpen={feedback.open}
        centered
        toggle={() => setFeedback({ ...feedback, open: false })}
      >
        <ModalBody className="text-center p-4">
          <div style={{ fontSize: 50, marginBottom: 15 }}>
            {feedback.type === "warning" ? (
              "⚠️"
            ) : feedback.type === "success" ? (
              <FontAwesomeIcon icon={faPaperPlane} />
            ) : (
              "❌"
            )}
          </div>

          <h4>{feedback.title}</h4>

          <p
            style={{
              color: "#666",
              marginTop: 15,
            }}
          >
            {feedback.message}
          </p>
        </ModalBody>
        <ModalFooter
          style={{
            justifyContent: "center",
            borderTop: "none",
          }}
        >
          <Button
            color="secondary"
            onClick={() => setFeedback({ ...feedback, open: false })}
          >
            {feedback.type === "success" ? "Fechar" : "Tentar novamente"}
          </Button>
        </ModalFooter>
      </Modal>
    </FormWrapperForm>
  );
}
